import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuftragSuche, useMe, useMeineStunden } from '../hooks/data.js';
import { StundenSheet } from '../sheets/StundenSheet.jsx';
import { RegieSheet } from '../sheets/RegieSheet.jsx';
import { AbwesenheitSheet } from '../sheets/AbwesenheitSheet.jsx';
import { useFunktionen } from '../hooks/useFunktionen.js';
import { useBegriffe } from '../hooks/useBegriffe.js';
import { useTimer } from '../state/timer.jsx';
import { recentAuftraege } from '../lib/storage.js';
import { clock, hours, initials, isoDate } from '../lib/format.js';
import { Icon, StopIcon } from '../components/Icon.jsx';
import { KontoSheet } from '../sheets/KontoSheet.jsx';
import { OutboxBanner } from '../state/outbox.jsx';

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const DATE = new Intl.DateTimeFormat('de-CH', { weekday: 'long', day: 'numeric', month: 'long' });

/** Einstieg: zuerst den Auftrag finden – alles andere läuft danach auf diesen Auftrag. */
export function SearchPage() {
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim(), 600); // jede Suche ist eine Exxas-Anfrage → erst nach kurzer Tipppause
  const search = useAuftragSuche(term);
  const me = useMe();
  const b = useBegriffe();
  const timer = useTimer();
  const navigate = useNavigate();
  const recent = recentAuftraege();
  const [konto, setKonto] = useState(false);
  const [stundenOffen, setStundenOffen] = useState(false);
  const [regieOffen, setRegieOffen] = useState(false);
  const [abwesenheitOffen, setAbwesenheitOffen] = useState(false);
  const hat = useFunktionen();
  const woche = useMeineStunden(isoDate(), { laden: !!me.data && hat('meineStunden') });
  const heute = woche.data?.tage?.find((t) => t.datum === isoDate());

  const open = (nummer) => navigate(`/auftrag/${encodeURIComponent(nummer)}`);
  const onSubmit = (e) => {
    e.preventDefault();
    const first = search.data?.[0];
    if (first) open(first.nummer);
  };

  return (
    <div className="app">
      <div className="scroll">
        <div className="swrap">
          <div className="between">
            <div className="stack" style={{ gap: 2 }}>
              <span className="sub" style={{ fontWeight: 600 }}>{DATE.format(new Date())}</span>
              <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>{b('auftrag')} wählen</h1>
            </div>
            <button className="avatar lg" style={{ border: 'none', cursor: 'pointer' }} aria-label={`Angemeldet als ${me.data?.name ?? '…'} – Konto und Abmelden`} onClick={() => setKonto(true)}>{initials(me.data?.name)}</button>
          </div>

          <form onSubmit={onSubmit} role="search">
            <label className="search big">
              <Icon name="search" size={22} stroke={2.2} />
              <span className="vh">Auftrag suchen</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nummer, Objekt, Kunde oder Ort"
                autoComplete="off"
                enterKeyHint="search"
                autoFocus
              />
              {q && (
                <button type="button" className="iconbtn" aria-label="Suche leeren" onClick={() => setQ('')}>
                  <Icon name="x" size={18} stroke={2.4} />
                </button>
              )}
            </label>
          </form>

          <OutboxBanner />
          {timer.running && (
            <div className="card-dark" style={{ flexDirection: 'row', alignItems: 'center', padding: '14px 14px 14px 18px', borderRadius: 20 }}>
              <span className="dot live" />
              <button onClick={() => open(timer.nummer)} className="plainbtn">
                <span className="ellipsis on-dark-muted" style={{ fontSize: 12, fontWeight: 600 }}>Zeit läuft · {timer.nummer} · {timer.titel}</span>
                <span className="tn" style={{ fontSize: 24, fontWeight: 800 }}>{clock(timer.elapsedSec)}</span>
              </button>
              <button className="btn stop" onClick={timer.stop} disabled={timer.busy} aria-label="Zeit stoppen und buchen">
                <StopIcon size={14} />Stopp
              </button>
            </div>
          )}

          {!q && hat('meineStunden') && (
            <button className="card" onClick={() => setStundenOffen(true)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: '12px 16px', textAlign: 'left', border: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', width: '100%' }} aria-label="Meine Stunden anzeigen">
              <Icon name="clock" size={22} />
              <span className="stack" style={{ gap: 0, flex: 1 }}>
                <span className="label" style={{ fontSize: 12, color: 'var(--muted-2)' }}>Meine Stunden</span>
                <span className="tn" style={{ fontSize: 16, fontWeight: 800 }}>
                  {woche.data ? `Heute ${hours(heute?.stunden ?? 0)} · Woche ${hours(woche.data.total)}` : woche.isError ? 'Nicht verfügbar – antippen' : 'Lädt …'}
                </span>
              </span>
              <Icon name="chevron" size={18} />
            </button>
          )}

          {!q && (hat('regie') || hat('abwesenheiten')) && (
            <div className="two" style={{ gap: 10 }}>
              {hat('regie') && (
                <button className="btn secondary" type="button" onClick={() => setRegieOffen(true)} style={{ width: '100%' }}>
                  <Icon name="plus" size={18} stroke={2.4} />Regierapport erstellen
                </button>
              )}
              {hat('abwesenheiten') && (
                <button className="btn secondary" type="button" onClick={() => setAbwesenheitOffen(true)} style={{ width: '100%' }}>
                  <Icon name="cal" size={18} stroke={2.4} />Ferien / Abwesenheit
                </button>
              )}
            </div>
          )}

          {term.length >= 2 ? (
            <Results query={search} onOpen={open} timerNr={timer.running ? timer.nummer : null} />
          ) : (
            <>
              {recent.length > 0 && (
                <Section title="Zuletzt geöffnet">
                  {recent.map((a) => (
                    <AuftragCard key={a.nummer} a={{ nummer: a.nummer, titel: a.titel, kunde: { firma: a.firma, ort: a.ort } }} onOpen={open} running={timer.running && timer.nummer === a.nummer} />
                  ))}
                </Section>
              )}
              {recent.length === 0 && (
                <div className="empty">Suche nach Auftragsnummer, Objekt, Kunde oder Ort. Geöffnete Aufträge erscheinen danach hier.</div>
              )}
            </>
          )}
        </div>
      </div>
      {konto && <KontoSheet onClose={() => setKonto(false)} />}
      {stundenOffen && <StundenSheet onClose={() => setStundenOffen(false)} />}
      {regieOffen && <RegieSheet onClose={() => setRegieOffen(false)} />}
      {abwesenheitOffen && <AbwesenheitSheet onClose={() => setAbwesenheitOffen(false)} />}
    </div>
  );
}

function Results({ query, onOpen, timerNr }) {
  if (query.isLoading) {
    return <div className="alist">{[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 78 }} />)}</div>;
  }
  if (query.isError) return <div className="empty" role="alert">{query.error.message}</div>;
  const list = query.data ?? [];
  if (!list.length) return <div className="empty">Kein aktiver Auftrag gefunden. Versuche Nummer, Strasse oder Kunde.</div>;
  return (
    <Section title={`${list.length} Treffer`}>
      {list.map((a) => <AuftragCard key={a.nummer} a={a} onOpen={onOpen} running={timerNr === a.nummer} />)}
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <section className="stack" style={{ gap: 8 }}>
      <h2 className="eyebrow" style={{ margin: '0 4px' }}>{title}</h2>
      <div className="alist">{children}</div>
    </section>
  );
}

function AuftragCard({ a, onOpen, running }) {
  const typ = a.typ === 's' ? 'Ticket' : a.typ === 'p' ? 'Projekt' : 'Auftrag';
  return (
    <button className="acard" onClick={() => onOpen(a.nummer)}>
      <span className="badge" style={running ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)' } : undefined}>
        <span style={{ fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{running ? 'Läuft' : typ}</span>
        <Icon name={running ? 'clock' : a.typ === 's' ? 'extra' : 'home'} size={18} />
      </span>
      <span className="stack" style={{ flex: 1, gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)' }}>{a.nummer}</span>
        <span className="ellipsis" style={{ fontSize: 16, fontWeight: 800 }}>{a.titel}</span>
        <span className="ellipsis sub">{[a.kunde?.firma, a.kunde?.ort].filter(Boolean).join(' · ')}</span>
      </span>
      <Icon name="chevron" size={20} stroke={2.2} style={{ color: 'var(--faint)' }} />
    </button>
  );
}
